<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import BackButton from 'dashboard/components/widgets/BackButton.vue';
import { isFileOnlyKnowledgebase } from './composables/useImmutableSchemaKnowledgebase';

const props = defineProps({
  name: {
    default: '',
    type: String,
  },
  knowledgebase: {
    type: Object,
    default: () => ({}),
  },
  showBackButton: {
    type: Boolean,
    default: false,
  },
  backUrl: {
    type: [String, Object],
    default: '',
  },
  backButtonLabel: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['edit', 'search']);

const { t } = useI18n();

const showSearch = computed(() => !isFileOnlyKnowledgebase(props.knowledgebase));

const icon = computed(() => {
  switch (props.knowledgebase.type) {
    case 'documents':
      return 'i-lucide-file';
    case 'event_documents':
      return 'i-lucide-file-signature';
    case 'catalogue':
      return 'i-lucide-layout-grid';
    case 'gallery':
      return 'i-lucide-book-image';
    default:
      return 'i-lucide-folder';
  }
});


const onEdit = () => {
  emit('edit');
};

const onSearch = () => {
  emit('search');
};
</script>

<template>
  <div
    class="flex justify-between items-center h-14 min-h-[3.5rem] py-2 bg-white dark:bg-slate-900 border-b border-slate-50 dark:border-slate-800/50 gap-4 pt-4 px-5"
  >
    <h1
      class="text-2xl gap-1 mb-0 flex items-center text-slate-900 dark:text-slate-100 truncate"
    >
      <BackButton
        v-if="showBackButton"
        :button-label="backButtonLabel"
        :back-url="backUrl"
        class="mr-4"
      />
      <Icon
        :icon="icon"
        class="mr-2 ml-1 rtl:ml-2 rtl:mr-1 hidden md:block header--icon"
      />
      <slot />
      <span
        class="text-slate-900 font-medium text-2xl dark:text-slate-100 truncate"
      >
        {{ name }}
      </span>
      <NextButton
        icon="i-lucide-pen-line"
        ghost
        slate
        class="ml-2"
        @click="onEdit"
      />
    </h1>

    <!-- Fake Search Bar -->
    <div v-if="showSearch" class="flex items-center gap-2">
      <div
        class="relative flex items-center cursor-pointer group"
        @click="onSearch"
      >

        <div
          class="flex items-center gap-2 px-3 py-2 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors min-w-[200px]"
        >
          <Icon
            icon="i-lucide-search"
            class="h-4 w-4 text-slate-400 group-hover:text-slate-500 dark:group-hover:text-slate-300"
          />
          <span class="text-sm text-slate-500 dark:text-slate-400 select-none">
            {{ t('LIBRARIES.SEARCH.PLACEHOLDER') }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>
